/**
 * (c) LBS logics
 *
 *     Mobile Business Technology GmbH
 *     4020 Linz, Austria
 *
 *     www.lbs-logics.com
 *
 *     created on 201906029 by Jan Brunner
 *     all rights reserved.
 */

import {
  Injectable,
  NgZone,
  Output,
  ErrorHandler,
  EventEmitter,
  Injector,
} from '@angular/core';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { BehaviorSubject, filter, firstValueFrom, lastValueFrom } from 'rxjs';
import { IMSId } from 'src/app/_model/ims-object';
import { ExtFilesService } from '../ext-files.service';
import {
  ExtFile,
  ExtFileDTO,
  ExtFileDownloadStatus,
} from 'src/app/_model/ext-file';
import { ExtFilesDaoService } from '../../dao/ext-files-dao.service';

import { CommunicationService } from 'src/app/_services/communication/logic/commumication.service';
import { CredentialsService } from 'src/app/_services/credentials/logic/credentials.service';
import { AppService } from 'src/app/_services/app-service/logic/app.service';
import { ExtFileRemoteActionBuilder } from 'src/app/_model/remote-action/remote-action-builder/ext-file-remote-action-builder/ext-file-remoteaction-builder';
import { FileManagerService } from 'src/app/_services/file-manager/file-manager.service';

// import { Plugins } from '@capacitor/core';
// const { Downloader } = Plugins;
import { isUndefinedOrNull } from 'src/app/_util/lbs-utils';
import { InitializeStatus } from 'src/app/_services/initialize/initialize-aware';
import { AppParameterService } from 'src/app/_services/app-parameter/logic/app-parameter.service';

import mime from 'mime';
import { Share } from '@capacitor/share';
import { ExtFileError } from '../ext-file-errors';
import { AuthenticationService } from 'src/app/_services/authentication/authentication.service';
import { RemoteActionService } from 'src/app/_services/remote-action/logic/remote-action.service';
import { RemoteAction } from 'src/app/_model/remote-action/remote-action';
import { APILevel } from 'src/app/_services/sync/api-level';
import { LBSDownloader } from 'plugins/lbs-downloader/dist/esm';
import { ServicejobsService } from 'src/app/_services/servicejobs/logic/servicejobs.service';
import { Servicejob } from 'src/app/_model/servicejob';
import { ImsImage } from 'src/app/_model/ims-image';
import { IMSObject, IMSObjectType } from 'src/app/_model/ims-object';

const SHARE_CANCELED = 'Share canceled';

const EXT_FILES_FOLDER = 'extFiles';
const THUMBNAILS_FOLDER = 'thumbnails';
export const TEMP_FILES_FOLDER = 'temp';

type TempFileEvent = {
  downloadKey: number;
  success: boolean;
};

/**
 * Loads App Parameters
 */
@Injectable({
  providedIn: 'root', // Ensure it's a singleton!
})
export class ExtFilesLogicService implements ExtFilesService {
  // Public Member
  public extFiles$ = new BehaviorSubject<ExtFile[]>([]);
  public initializeStatus$: BehaviorSubject<InitializeStatus> =
    new BehaviorSubject(InitializeStatus.NOT_INITIALIZED);

  // Private Member
  private temporaryExtFiles: ExtFile[];
  private tempFileDownloaded: EventEmitter<TempFileEvent> = new EventEmitter();

  // Lifecycle
  constructor(
    private logger: LoggingService,
    private extFileDaoService: ExtFilesDaoService,
    private commService: CommunicationService,
    private appService: AppService,
    private credentialsService: CredentialsService,
    private fileManager: FileManagerService,
    private appParameterService: AppParameterService,
    private authenticationService: AuthenticationService,
    private remoteActionService: RemoteActionService,
    private servicejobsService: ServicejobsService,
    private injector: Injector,
    private zone: NgZone
  ) {
    this.initialize();

    this.zone.run(async () => {
      // await LBSDownloader.addListener(
      //   'CAPDownloaderDownloadUpdate',
      //   (info: any) => {
      //     this.zone.run(() => {
      //       this.updateDownload(info);
      //     });
      //   }
      // );
      await LBSDownloader.addListener(
        'CAPDownloaderDownloadFinished',
        (info: any) => {
          this.logger.log('LBSDownloader finished download with info', info);
          this.zone.run(() => {
            this.finishDownload(info);
          });
        }
      );
      await LBSDownloader.addListener(
        'CAPDownloaderDownloadError',
        (info: any) => {
          this.zone.run(() => {
            this.failedDownload(info);
          });
        }
      );
    });
  }

  // Public Functions
  public getExtFilesForId(id: IMSId): ExtFile[] {
    if (id === undefined) {
      return undefined;
    }

    return this.extFiles$.getValue().filter((file) => {
      if (file.ownerid === id.imsId && file.objref === id.objRef) {
        return file;
      }
      return undefined;
    });
  }

  public getExtFileByExtFileId(extFileId: string): ExtFile {
    if (extFileId === undefined) {
      return undefined;
    }

    return this.extFiles$.getValue().find((file) => {
      if (file.extfileid === extFileId) {
        return true;
      }
      return false;
    });
  }

  public getPhotoDocuExtFilesForId(id: IMSId): ExtFile[] {
    if (id === undefined) {
      return undefined;
    }

    if (id.objRef === 'ims/Pin') {
      return this.extFiles$.getValue().filter((file) => {
        if (
          file.pin?.id.imsId === id.imsId &&
          file.extfiletypesyscode === 'PHOTODOC'
        ) {
          return file;
        }
        return undefined;
      });
    } else {
      return this.extFiles$.getValue().filter((file) => {
        if (
          file.ownerid === id.imsId &&
          file.objref === id.objRef &&
          file.extfiletypesyscode === 'PHOTODOC'
        ) {
          return file;
        }
        return undefined;
      });
    }
  }

  public getExtFileById(id: IMSId): ExtFile {
    if (id) {
      return this.extFiles$.getValue().find((sj) => {
        if (sj.id.imsId === id.imsId && sj.id.secondaryId === id.secondaryId) {
          return true;
        }
        return false;
      });
    } else {
      return undefined;
    }
  }

  public async downloadExtFileManual(extFile: ExtFile) {
    let response = await this.downloadExtFile(extFile);
    extFile.downloadStatus = response.status;
    extFile.downloadProgress = 0;
    extFile.downloadKey = response.downloadKey;
    await this.updateExtFileDownloadStatus(extFile);
  }

  public async downloadTempFile(
    extFileId: number,
    tempFileName: string
  ): Promise<boolean> {
    let efrab = new ExtFileRemoteActionBuilder(
      extFileId,
      this.appService,
      this.credentialsService,
      this.logger
    );
    let remoteAction = await efrab.generateRemoteAction([]);
    let request = await this.commService.getServerRequest(remoteAction);

    let response = await LBSDownloader.queueDownload({
      directory: TEMP_FILES_FOLDER,
      name: tempFileName,
      address: request.url,
      headers: request.headers,
      body: JSON.stringify(request.body),
    });

    console.log('JBDownloadDriveLog', response.downloadKey);
    return new Promise<boolean>((resolve, reject) => {
      this.tempFileDownloaded.subscribe({
        next: (it: TempFileEvent) => {
          if (it.downloadKey == response.downloadKey) {
            console.log(
              'JBDownloadTemp download result for correct downloadkey',
              it.success
            );
            if (it.success) {
              resolve(true);
            } else {
              resolve(false);
            }
          }
        },
      });
    });
  }

  public async deleteExtFileLocally(extFile: ExtFile, deleteThumb: boolean = false) {
    try {
      await this.fileManager.deleteExtFile(extFile);
    } catch (e) {
      if (e.message !== FileManagerService.FILE_NOT_FOUND_ERR) {
        // Only abort this functions if the error is not
        // a File not found error, because if the file
        // couldnt be found deletion was a success anyhow
        return;
      }
    }
    if (deleteThumb) {
      try {
        await this.fileManager.deleteThumbnailExtFile(extFile)
      } catch (e) {
        if (e.message !== FileManagerService.FILE_NOT_FOUND_ERR) {
          // Only abort this functions if the error is not
          // a File not found error, because if the file
          // couldnt be found deletion was a success anyhow
          return;
        }
      }
    }
    extFile.downloadStatus = 'Deleted';
    extFile.downloadProgress = undefined;
    extFile.downloadKey = undefined;
    await this.updateExtFileDownloadStatus(extFile);
  }

  public async shareExtFile(extFile: ExtFile): Promise<void> {
    let uri = await this.fileManager.getExtFileUri(extFile);
    const mimeType = mime.getType(extFile.fileextension);
    this.logger.log(
      this.constructor.name,
      'share file with extension',
      extFile.fileextension,
      ', foundmimetype',
      mimeType
    );
    try {
      let result = await Share.share({
        title: extFile.label,
        text: extFile.label,
        url: uri,
      });
      this.logger.log(
        this.constructor.name,
        'extfile shared with app:',
        result?.activityType
      );
    } catch (e) {
      if (e.message === SHARE_CANCELED) {
        this.logger.log(this.constructor.name, 'Share was canceled');
      } else {
        this.logger.error(
          this.constructor.name,
          'Could not share extfile',
          extFile?.getImsId(),
          e,
          e?.message
        );
        throw new Error(ExtFileError.COULD_NOT_SHARE);
      }
    }
  }

  public async checkDownloadedAndUpdate(extFile: ExtFile): Promise<boolean> {
    if (extFile == null) {
      return false;
    }

    if (extFile.isLocal()) {
      return true;
    }

    let exists = await this.fileManager.extFileExists(extFile);
    if (extFile.downloadStatus === 'Downloaded') {
      if (exists) {
        return true;
      } else if (extFile.synctype === 'NEVER') {
        this.logger.log(
          this.constructor.name,
          'checkDownloadedAndUpdate - file had status downloaded but couldnt be found on system -> should never be downloaded - set to not available'
        );
        extFile.downloadStatus = 'NotAvailable';
        await this.updateExtFileDownloadStatus(extFile);
        return false;
      } else {
        this.logger.log(
          this.constructor.name,
          'checkDownloadedAndUpdate - file had status downloaded but couldnt be found on system -> set to not downloaded'
        );
        extFile.downloadStatus = 'NotDownloaded';
        await this.updateExtFileDownloadStatus(extFile);
        return false;
      }
    } else {
      if (exists) {
        this.logger.log(
          this.constructor.name,
          'checkDownloadedAndUpdate - file had not status downloaded but could be found on system -> set to not available'
        );
        extFile.downloadStatus = 'Downloaded';
        await this.updateExtFileDownloadStatus(extFile);
        return true;
      } else {
        return false;
      }
    }
  }

  public async downloadThumbIfNecessary(extFile: ExtFile): Promise<void> {
    if (extFile == null) {
      return;
    }

    if (extFile.extfiletypesyscode !== 'PHOTODOC') {
      return;
    }

    if (extFile.isLocal()) {
      return;
    }

    let exists = await this.fileManager.extFileThumbExists(extFile);
    if (exists) {
      return;
    }
    await this.downloadThumbExtFile(extFile);
    
    // if (extFile.thumbDownloadStatus === 'Downloaded') {
    //   if (exists) {
    //     return;
    //   } else if (extFile.synctype === 'NEVER') {
    //     this.logger.log(
    //       this.constructor.name,
    //       'checkDownloadedAndUpdate - file had status downloaded but couldnt be found on system -> should never be downloaded - set to not available'
    //     );
    //     extFile.downloadStatus = 'NotAvailable';
    //     await this.updateThumbExtFileDownloadStatus(extFile);
    //     return false;
    //   } else {
    //     this.logger.log(
    //       this.constructor.name,
    //       'checkDownloadedAndUpdate - file had status downloaded but couldnt be found on system -> set to not downloaded'
    //     );
    //     extFile.downloadStatus = 'NotDownloaded';
    //     await this.updateExtFileDownloadStatus(extFile);
    //     return false;
    //   }
    // } else {
    //   if (exists) {
    //     this.logger.log(
    //       this.constructor.name,
    //       'checkDownloadedAndUpdate - file had not status downloaded but could be found on system -> set to not available'
    //     );
    //     extFile.downloadStatus = 'Downloaded';
    //     await this.updateExtFileDownloadStatus(extFile);
    //     return true;
    //   } else {
    //     return false;
    //   }
    // }
  }

  public async createPhotoDocuExtFile(
    image: ImsImage,
    extFileDto: ExtFileDTO
  ): Promise<ExtFile> {
    const localExtFile = ExtFile.fromDto(extFileDto);
    const savedExtFile = await this.extFileDaoService.createExtFile(
      localExtFile
    );
    const newFilePath = await this.fileManager.copyFileToLocalPhotoDirectory(
      image,
      savedExtFile
    );
    await this.loadLocalExtFiles();
    return savedExtFile;
  }

  // Resetable
  public async reset() {
    await this.extFileDaoService.reset();
    await this.fileManager.deleteAllExtFiles();
    await this.loadLocalExtFiles();
    this.checkAndDownloadExtFiles();
  }

  public async clear() {
    await this.extFileDaoService.clear();
    this.extFiles$.next([]);
    await this.fileManager.deleteAllExtFiles();
  }

  // The consumed-draft bookkeeping and the cloud draft list moved to the
  // ActivityConfirmationsService, so there is nothing draft related to clear here.

  // InitializeAware
  async initialize(): Promise<void> {
    this.initializeStatus$.next(InitializeStatus.INITIALIZING);
    try {
      await this.loadLocalExtFiles();
      this.initializeStatus$.next(InitializeStatus.INITIALIZED);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local data: ',
        error?.message
      );
      this.initializeStatus$.next(InitializeStatus.FAILED_INITIALIZE);
    }
  }

  // Syncable

  public async presync(): Promise<void> {
    this.temporaryExtFiles = this.extFiles$.getValue();
  }
  public async sync(apiLevel: APILevel, syncKey: string): Promise<void> {
    await this.loadRemoteExtFiles(apiLevel, syncKey);
  }
  public async postsync(): Promise<void> {
    const localExtFiles = this.extFiles$.getValue().filter((ef) => {
      return ef.isLocal();
    });
    this.logger.log(
      this.constructor.name,
      'JBPDFV2 postsync',
      'Executing postsync -> merge local extfiles with remote extfiles by extfileid',
      localExtFiles.length,
      this.extFiles$.getValue().length
    );
  }

  // Private Functions
  private async updateDownload(info: any) {
    this.logger.log(this.constructor.name, 'Update Download with', info);
    let extFile = this.getExtFileById(
      this.getIdFromDownloadKey(info.downloadKey)
    );

    if (isUndefinedOrNull(extFile)) {
      this.logger.log(
        this.constructor.name,
        'Extfile with downloadkey',
        info,
        'is not available anymore - cannot set status to downloaded'
      );
      return;
    }

    extFile.downloadStatus = 'Downloading';
    extFile.downloadProgress = info.progess;

    this.updateExtFileDownloadStatus(extFile);
  }

  private async finishDownload(info: any) {
    this.logger.log(
      this.constructor.name,
      'Finish Download with',
      info?.downloadKey
    );
    let extFile = this.getExtFileById(
      this.getIdFromDownloadKey(info.downloadKey)
    );

    if (isUndefinedOrNull(extFile)) {
      this.logger.log(
        this.constructor.name,
        'Extfile with downloadkey',
        info,
        'is not available - might be deleted or is a temporary file or a thumbnail'
      );
      this.tempFileDownloaded.emit({
        downloadKey: info?.downloadKey,
        success: true,
      });
      return;
    }

    const valid = await this.fileManager.extFileExists(extFile);

    if (valid) {
      this.logger.log(
        this.constructor.name,
        'set downloadStatus to downloaded for ',
        extFile.localFileName
      );

      extFile.downloadStatus = 'Downloaded';
    } else {
      this.logger.log(
        this.constructor.name,
        'set downloadStatus to failed for ',
        extFile.localFileName,
        'as local file is not valid'
      );

      extFile.downloadStatus = 'FailedDownload';
    }

    extFile.downloadProgress = undefined;

    this.updateExtFileDownloadStatus(extFile);
  }

  private async failedDownload(info: any) {
    this.logger.log(this.constructor.name, 'Failed Download with', info);
    let extFile = this.getExtFileById(
      this.getIdFromDownloadKey(+info.downloadKey)
    );

    if (isUndefinedOrNull(extFile)) {
      this.logger.log(
        this.constructor.name,
        'Extfile with downloadkey',
        info,
        'is not available - might be deleted or is a temporary file'
      );

      this.tempFileDownloaded.emit({
        downloadKey: info?.downloadKey,
        success: false,
      });
      return;
    }

    extFile.downloadStatus = 'FailedDownload';
    extFile.downloadProgress = undefined;

    this.updateExtFileDownloadStatus(extFile);
  }

  private getIdFromDownloadKey(downloadKey: number): IMSId {
    if (downloadKey) {
      const extFile = this.extFiles$.getValue().find((obj) => {
        return obj.downloadKey == downloadKey;
      });
      return extFile?.id;
    }

    return undefined;
  }

  private async updateExtFileDownloadStatus(extFile: ExtFile) {
    await this.extFileDaoService.updateDownloadStatus(extFile);
    await this.loadLocalExtFiles();
  }

  private async loadLocalExtFiles() {
    try {
      this.logger.log(this.constructor.name, '- Load local ExtFiles');
      let extFiles: ExtFile[] = await this.extFileDaoService.getExtFiles();
      this.logger.log(
        this.constructor.name,
        '- Loaded',
        extFiles.length,
        'local ExtFiles'
      );

      this.extFiles$.next(extFiles);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local ExtFiles: ',
        error?.message
      );
      throw error;
    }
  }

  private async checkAndDownloadExtFiles(newExtFiles?: ExtFile[]) {
    let extFiles: ExtFile[];
    if (newExtFiles) {
      extFiles = newExtFiles;
      this.logger.log(
        this.constructor.name,
        'checkAndDownloadExtFiles for new ExtFiles:',
        extFiles.length
      );
    } else {
      extFiles = this.extFiles$.getValue();
      this.logger.log(
        this.constructor.name,
        'checkAndDownloadExtFiles for all ExtFiles:',
        extFiles.length
      );
    }
    for (let index = 0; index < extFiles.length; index++) {
      this.logger.log(
        this.constructor.name,
        'checkAndDownloadExtFiles check file status:',
        extFiles[index].localFileName
      );
      const extFile = extFiles[index];
      await this.checkDownloadedAndUpdate(extFile);
      try {
        if (
          extFile.synctype === 'ALWAYS' &&
          !(
            extFile.downloadStatus === 'Downloaded' ||
            extFile.downloadStatus === 'Downloading' ||
            extFile.downloadStatus === 'Deleted' ||
            extFile.downloadStatus === 'Local'
          )
        ) {
          this.logger.log(
            this.constructor.name,
            'checkAndDownloadExtFiles',
            extFile.localFileName,
            'is synctype always and is not downloaded - initiate download'
          );
          let response = await this.downloadExtFile(extFile);
          this.logger.log(
            this.constructor.name,
            'checkAndDownloadExtFiles',
            extFile.localFileName,
            'started download with status',
            response.status,
            'and downloadkey',
            response.downloadKey
          );

          extFile.downloadStatus = response.status;
          extFile.downloadKey = response.downloadKey;

          await this.updateExtFileDownloadStatus(extFile);
        }

        if (extFile.extfiletypesyscode === 'PHOTODOC' && (extFile.synctype === 'LAZY' || extFile.synctype === 'ALWAYS')) {

        }
      } catch (e) {
        this.logger.log(
          this.constructor.name,
          'checkAndDownloadExtFiles failed for file:',
          extFile.localFileName,
          '- set to failedDownload'
        );
        extFile.downloadStatus = 'FailedDownload';
        this.updateExtFileDownloadStatus(extFile);
      }

      await this.downloadThumbIfNecessary(extFile);
    }
  }

  private async downloadExtFile(
    extFile: ExtFile,
    maxWidth?: number,
    maxHeight?: number
  ): Promise<{ status: ExtFileDownloadStatus; downloadKey?: number }> {
    if (extFile.synctype === 'NEVER') {
      return { status: 'NotAvailable' };
    }
    this.logger.log(this.constructor.name, '- Download file:', extFile.label);

    // remove any artifacts before redownload (ios does not overwrite files)
    try {
      await this.fileManager.deleteExtFile(extFile);
    } catch (e) {
      this.logger.log(
        this.constructor.name,
        'error while cleaning up artifacts before download - ignore and continue',
        e?.message
      );
    }

    let efrab = new ExtFileRemoteActionBuilder(
      extFile,
      this.appService,
      this.credentialsService,
      this.logger,
      maxWidth,
      maxHeight
    );
    let remoteAction = await efrab.generateRemoteAction([]);
    let request = await this.commService.getServerRequest(remoteAction);

    try {
      this.logger.log(
        this.constructor.name,
        '- Downloader Request',
        request.body
      );
      let response = await LBSDownloader.queueDownload({
        directory: EXT_FILES_FOLDER,
        name: extFile.localFileName,
        address: request.url,
        headers: request.headers,
        body: JSON.stringify(request.body),
      });
      this.logger.log(
        this.constructor.name,
        '- Downloader Key',
        response.downloadKey
      );
      return { status: 'Downloading', downloadKey: response.downloadKey };
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        '- Downloader Error',
        error?.message
      );
      return { status: 'FailedDownload' };
    }
  }

  private async downloadThumbExtFile(
    extFile: ExtFile
  ): Promise<{ status: ExtFileDownloadStatus; downloadKey?: number }> {
    if (extFile.synctype === 'NEVER') {
      return { status: 'NotAvailable' };
    }

    const appParams = this.appParameterService.appParameters$.getValue();
    const thumbMaxWidth = appParams?.['photodoc.thumb.maxwidth'] ?? 200;
    const thumbMaxHeight = appParams?.['photodoc.thumb.maxheight'] ?? 200;

    this.logger.log(
      this.constructor.name,
      '- Download thumb:',
      extFile.label,
      'with maxwidth',
      thumbMaxWidth,
      'and maxheight',
      thumbMaxHeight
    );

    // remove any artifacts before redownload (ios does not overwrite files)
    try {
      await this.fileManager.deleteThumbnailExtFile(extFile);
    } catch (e) {
      this.logger.log(
        this.constructor.name,
        'error while cleaning up artifacts before download - ignore and continue',
        e?.message
      );
    }

    let efrab = new ExtFileRemoteActionBuilder(
      extFile,
      this.appService,
      this.credentialsService,
      this.logger,
      thumbMaxWidth,
      thumbMaxHeight
    );
    let remoteAction = await efrab.generateRemoteAction([]);
    let request = await this.commService.getServerRequest(remoteAction);

    try {
      this.logger.log(
        this.constructor.name,
        '- Downloader Request',
        request.body
      );
      let response = await LBSDownloader.queueDownload({
        directory: THUMBNAILS_FOLDER,
        name: extFile.localFileName,
        address: request.url,
        headers: request.headers,
        body: JSON.stringify(request.body),
      });
      this.logger.log(
        this.constructor.name,
        '- Downloader Key',
        response.downloadKey
      );
      return { status: 'Downloading', downloadKey: response.downloadKey };
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        '- Downloader Error',
        error?.message
      );
      return { status: 'FailedDownload' };
    }
  }

  async initializeCompleted(): Promise<void> {
    this.checkAndDownloadExtFiles();
  }

  private async loadRemoteExtFiles(apiLevel: APILevel, syncKey: string) {
    if (this.authenticationService.authenticationState.getValue()) {
      this.logger.log(
        this.constructor.name,
        'loadRemoteExtFiles - Load remote Materials from IMS'
      );

      try {
        let newExtFiles: ExtFile[] = await this.loadRemoteExtFileBatch(
          syncKey,
          []
        );

        this.logger.log(
          this.constructor.name,
          'JBExtFileSync loadRemoteExtFiles - Finished loading' +
            newExtFiles.length +
            ' new extfiles'
        );

        const removedExtFiles = this.temporaryExtFiles
          .filter(
            (x) =>
              newExtFiles.findIndex(
                (it) => it.localFileName === x.localFileName
              ) < 0
          )
          .filter((x) => !x.isLocal());
        const addedExtFiles = newExtFiles.filter(
          (x) =>
            this.temporaryExtFiles.findIndex(
              (it) => it.localFileName === x.localFileName
            ) < 0
        );

        const localExtFiles = this.extFiles$.getValue().filter((ef) => {
          return ef.isLocal();
        });
        const backSyncedLocalExtFiles = localExtFiles.filter((lef) => {
          return (
            addedExtFiles.findIndex((aef) => {
              return aef.extfileid === lef.extfileid;
            }) >= 0
          );
        });

        for (const backSyncedLocalExtFile of backSyncedLocalExtFiles) {
          this.logger.log(
            this.constructor.name,
            'JBExtFileSync loadRemoteExtFiles - Remove backsynced local extfile from addedExtFiles',
            backSyncedLocalExtFile.localFileName
          );
          await this.fileManager.copyLocalPhotoToCache(
            backSyncedLocalExtFile,
            addedExtFiles.find(
              (it) => it.extfileid == backSyncedLocalExtFile.extfileid
            )
          );
          await this.extFileDaoService.deleteLocalExtFile(
            backSyncedLocalExtFile
          );
          this.fileManager.deleteExtFile(backSyncedLocalExtFile);
        }

        console.log(
          'JBExtFileSync - had ',
          this.temporaryExtFiles.length,
          'files'
        );
        console.log('JBExtFileSync - now has ', newExtFiles.length, 'files');

        // No draft handling here anymore - that is entirely AC driven now.

        // Remove Extfiles not needed anymore
        console.log(
          'JBExtFileSync - ',
          removedExtFiles.length,
          'removed files'
        );
        for (const removedExtFile of removedExtFiles) {
          this.deleteExtFileLocally(removedExtFile, true);
        }
        await this.extFileDaoService.deleteExtFiles(removedExtFiles);
        await this.loadLocalExtFiles();

        // console.log(
        //   `JBMenuExtSync - extfileTypes found ${Array.from(
        //     this.extFiles$
        //       .getValue()
        //       .reduce((acc, extFile) => {
        //         if (extFile.extfiletypesyscode === 'PHOTODOC') {
        //           acc.add('PHOTO');
        //         } else if (extFile.enableplanmarker) {
        //           acc.add('PLAN');
        //         } else {
        //           acc.add('DOCUMENT')
        //         }
        //         return acc;
        //       }, new Set<String>())
        //       .values()
        //   ).join(', ')}`
        // );

        // Download new ExtFiles
        console.log('JBExtFileSync - ', addedExtFiles.length, 'new files');
        this.checkAndDownloadExtFiles(addedExtFiles);
      } catch (error) {
        this.logger.error(
          this.constructor.name,
          'loadRemoteExtFiles - Error downloading ExtFiles: ',
          error?.message
        );
        throw error;
      }
    }
  }

  // The cloud draft handling moved to the ActivityConfirmationsService: the draft
  // infos live on the AC now, and a draft whose ExtFile is never synced has to be
  // offered too. See ActivityConfirmationsService.cloudDraftInfos$ /
  // updateCloudDraftInfos() / markDraftConsumed().

  // updateExtFileFilledFormData() was removed - the draft payload lives on the
  // ActivityConfirmation now, see ActivityConfirmationsService.updateFilledFormData().

  // restoreCloudDraft() was removed: it had no callers, was keyed by extfileid and
  // would not work for an AC without a synced ExtFile. The takeover path in
  // modal-multi-instance-dyn-form / dyn-form.component is the live one.

  // restoreDraftFormData() was removed: draft data must come from the
  // ActivityConfirmations only. Restoring drafts here as well meant a draft could
  // show up twice - once auto-restored as a local form, once in the cloud draft
  // list. Takeover now happens explicitly via cloudDraftInfos$, and the cleanup of
  // finished chains moved to ActivityConfirmationsService.cleanupFinishedDraftChains().

  private async loadRemoteExtFileBatch(
    syncKey: string,
    extFiles: ExtFile[],
    nextPart?: string
  ): Promise<ExtFile[]> {
    console.log(
      this.constructor.name,
      'loadRemoteExtFileBatch - start loading batch with current ExtFiles',
      extFiles?.length,
      'and nextPart',
      nextPart
    );
    let extFilesData;

    if (nextPart) {
      extFilesData = await this.remoteActionService.sendRemoteAction(
        new RemoteAction({
          endpoint: 'syncnextpart',
          cachable: false,
          fifo: false,
          authenticationRequired: true,
          data: {
            nextid: nextPart,
            synckey: syncKey,
          },
        })
      );

      console.log(
        this.constructor.name,
        'loadRemoteExtFileBatch - received extfiles with nextpart request with',
        extFilesData?.length,
        'extfiles'
      );
    } else {
      const additionalData = {
        synckey: syncKey,
      };
      const additionalJobsIds: Servicejob[] =
        this.servicejobsService.servicejobs$.getValue() ?? [];

      additionalData['requestingAdditionalSjobs'] = additionalJobsIds
        .map((sjob) => sjob?.id?.imsId)
        .join(',');

      let remoteActionData = {
        endpoint: 'syncextfiles',
        cachable: false,
        fifo: false,
        authenticationRequired: true,
        data: additionalData,
      };

      extFilesData = await this.remoteActionService.sendRemoteAction(
        new RemoteAction(remoteActionData)
      );

      console.log(
        this.constructor.name,
        'loadRemoteExtFileBatch - received extFilesDataContainer with',
        extFilesData?.length,
        'extfiles'
      );
    }

    let nextId: string;
    let nextExtFiles: ExtFile[] = [];
    for (const extFileData of extFilesData) {
      if (extFileData['#nextid']) {
        nextId = extFileData['#nextid'];
      } else {
        nextExtFiles.push(
          ExtFile.fromDto(
            extFileData,
            undefined,
            this.appParameterService.getFormatLabelForObject('ims/ExtFile')
          )
        );
      }
    }

    // console.log(
    //   this.constructor.name,
    //   'loadRemoteExtFileBatch - Downloaded Batch ExtFiles: ',
    //   // extFilesData?.length,
    //   'Total'
    // );

    await this.extFileDaoService.saveExtFiles(nextExtFiles);

    let returnExtFiles = extFiles.concat(nextExtFiles);
    if (nextId) {
      console.log(
        this.constructor.name,
        'loadRemoteExtFileBatch - Continue to download more extfiles with nextid',
        nextId
      );
      return await this.loadRemoteExtFileBatch(syncKey, returnExtFiles, nextId);
    }

    return returnExtFiles;
  }
}
